/**
 * Custom scroll behavior for Nuxt router, supporting back/forward restoration,
 * hash anchor scrolling, and special checkout handling. Works in tandem with the scroll plugin.
 *
 * - Restore scroll on popstate (back/forward) using sessionStorage.
 * - Smooth-scrolls to hash navigation or scrolls to top on normal navigation.
 * - For "checkout" pages, always applies smooth scroll/reset after delay.
 */
import { Logger } from '~/helpers/logger';
import { getOrCreateScrollFlagNamespace, getScrollStorageKey } from '~/helpers/scrollNamespace';

const POPSTATE_KEY = 'is_popstate';

/**
 * Waits for the next animation frame.
 */
function waitForAnimationFrame() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(resolve);
  });
}

/**
 * Waits for the next paint (animation frame + setTimeout 0), for Safari/DOM flicker fixes.
 */
async function waitForNextPaint() {
  await waitForAnimationFrame();
  // We need to use a delay to reduce the chance of scrolling to the wrong position using back navigation.
  await new Promise((resolve) => setTimeout(resolve, 1000));
}

export default async function scrollBehavior(to, from, savedPosition) {
  if (!process.client) return { x: 0, y: 0 };
  // Use a symbol-based popstate flag (set/reset by plugin via scrollFlagNamespace)
  const scrollFlagNamespace = getOrCreateScrollFlagNamespace();
  const isPop = Boolean(scrollFlagNamespace && scrollFlagNamespace[POPSTATE_KEY]);
  if (scrollFlagNamespace) scrollFlagNamespace[POPSTATE_KEY] = false; // Reset after reading

  // Use a scroll key based on location.pathname + search + hash; always matches current SPA state, robust to queries/filters.
  const storageKey = 'nuxt:scroll:' + getScrollStorageKey();
  let storedY = null;

  try {
    const s = sessionStorage.getItem(storageKey);
    if (s !== null) {
      storedY = Number(s);
      sessionStorage.removeItem(storageKey);
    }
  } catch (e) {
    Logger.error('[scrollBehavior] sessionStorage read error', e);
  }

  // Special handling for "checkout" route (delayed, always smooth)
  if (to.name && to.name.includes('checkout')) {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (to.hash) {
          resolve({ selector: to.hash, behavior: 'smooth' });
          return;
        }

        resolve({ left: 0, top: 0, behavior: 'smooth' });
      }, 250);
    });
  }

  // 🧭 if navigation with back/forward → restoring scroll
  if (isPop && savedPosition) {
    await waitForNextPaint();
    if (storedY !== null && !Number.isNaN(storedY)) {
      window.scrollTo({ top: storedY, left: savedPosition.x || 0, behavior: 'auto' });
      return { x: savedPosition.x || 0, y: storedY };
    }
    window.scrollTo({ top: savedPosition.y, left: savedPosition.x || 0, behavior: 'auto' });
    return savedPosition;
  }

  // Hash anchor: smooth scroll
  if (to.hash) {
    await waitForNextPaint();
    return { selector: to.hash, behavior: 'smooth' };
  }

  // Default: scroll to top
  return { x: 0, y: 0 };
}
